import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Tag, X } from 'lucide-react';
// Deterministic HSL from a tag string so the same label always renders the
// same color. Keeps the chip visually distinct without needing a managed
// palette. Saturation/lightness chosen for legibility on the charcoal bg.
export const tagColor = (label) => {
    let h = 0;
    for (let i = 0; i < label.length; i++)
        h = (h * 31 + label.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    return {
        bg: `hsl(${hue} 60% 30% / 0.85)`,
        fg: `hsl(${hue} 80% 88%)`,
    };
};
const normalize = (s) => s.trim().toLowerCase();
export const TagInput = ({ value, onChange, suggestions, disabled = false, placeholder = 'Add tag…', maxTags = 10, }) => {
    const [draft, setDraft] = useState('');
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const filteredSuggestions = useMemo(() => {
        const q = normalize(draft);
        const selected = new Set(value.map(normalize));
        return suggestions
            .filter(s => !selected.has(normalize(s)))
            .filter(s => !q || normalize(s).includes(q))
            .slice(0, 6);
    }, [draft, suggestions, value]);
    const commit = useCallback((raw) => {
        const next = normalize(raw);
        if (!next)
            return;
        if (value.some(t => normalize(t) === next)) {
            setDraft('');
            return;
        }
        if (value.length >= maxTags)
            return;
        onChange([...value, next]);
        setDraft('');
    }, [value, onChange, maxTags]);
    const removeAt = useCallback((idx) => {
        onChange(value.filter((_, i) => i !== idx));
    }, [value, onChange]);
    const handleKeyDown = (e) => {
        var _a;
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(draft);
        }
        else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            e.preventDefault();
            removeAt(value.length - 1);
        }
        else if (e.key === 'Escape') {
            setDraft('');
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
    };
    const showSuggestions = focused && filteredSuggestions.length > 0;
    return (_jsxs("div", { className: "relative", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-1.5 px-3 py-1.5 rounded-full border", style: {
                    borderColor: 'var(--pewter)',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    minHeight: '32px',
                }, onClick: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, children: [_jsx(Tag, { size: 12, style: { color: 'var(--dew)', flexShrink: 0 } }), value.map((t, idx) => {
                        const c = tagColor(t);
                        return (_jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]", style: { backgroundColor: c.bg, color: c.fg }, children: [t, !disabled && (_jsx("button", { type: "button", onClick: (e) => { e.stopPropagation(); removeAt(idx); }, className: "hover:opacity-70", "aria-label": `Remove tag ${t}`, children: _jsx(X, { size: 10 }) }))] }, `${t}-${idx}`));
                    }), _jsx("input", { ref: inputRef, value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: handleKeyDown, onFocus: () => setFocused(true), onBlur: () => {
                            // Defer so a suggestion click can land before we hide the dropdown.
                            setTimeout(() => setFocused(false), 120);
                        }, placeholder: value.length === 0 ? placeholder : '', disabled: disabled || value.length >= maxTags, className: "flex-1 min-w-[80px] bg-transparent outline-none text-xs", style: { color: 'var(--sand)' } })] }), showSuggestions && (_jsxs("div", { className: "absolute left-0 right-0 mt-1 py-1 rounded shadow-lg z-50 max-h-[180px] overflow-y-auto", style: { backgroundColor: 'var(--charcoal)', border: '1px solid var(--pewter)' }, children: [filteredSuggestions.map(s => {
                        const c = tagColor(s);
                        return (_jsx("button", { type: "button", onMouseDown: (e) => e.preventDefault(), onClick: () => commit(s), className: "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-white/10 transition-colors", style: { color: 'var(--sand)' }, children: _jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded-full text-[11px]", style: { backgroundColor: c.bg, color: c.fg }, children: s }) }, s));
                    }), draft.trim() !== '' && !filteredSuggestions.some(s => normalize(s) === normalize(draft)) && (_jsxs("button", { type: "button", onMouseDown: (e) => e.preventDefault(), onClick: () => commit(draft), className: "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-white/10 transition-colors border-t", style: { color: 'var(--dew)', borderColor: 'var(--pewter)' }, children: ["Create new tag \u201C", normalize(draft), "\u201D"] }))] }))] }));
};
